import React from 'react';
import { useNavigate } from 'react-router-dom';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { Box, Stack, Typography } from '@mui/material';

export type Crumb = {
  label: string;
  /** 指定時はリンク（パス遷移）。未指定かつ onClick も無い場合は現在地＝テキスト表示 */
  to?: string;
  /** to の代わりにクリックハンドラを直接指定する場合に使用（コンソールの戻る等） */
  onClick?: () => void;
};

type Props = {
  items: Crumb[];
  /** 下マージン（既定 1.5） */
  mb?: number;
  /**
   * 先頭にホーム（家アイコン）リンクを表示するか（既定 true）。
   * ドメインTopへ戻る導線として全ドメインで統一する。
   */
  showHome?: boolean;
  /** ホームアイコンの遷移先（既定 '/'） */
  homeTo?: string;
};

/**
 * サイドバー非表示ドメインの共通パンくず（フレームなし・モダン）。
 * 先頭に家アイコンのホームリンク、続いて各階層、末尾に現在地を置く。
 * 例:  🏠 ›  契約管理  ›  契約申請一覧
 */
const DomainBreadcrumbs: React.FC<Props> = ({ items, mb = 1.5, showHome = true, homeTo = '/' }) => {
  const navigate = useNavigate();

  const Separator = (
    <ChevronRightIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0, mx: 0.25 }} />
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        mb,
        width: 'fit-content',
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      {showHome && (
        <>
          <Box
            component="button"
            onClick={() => navigate(homeTo)}
            aria-label="ホーム"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 0,
              p: 0.25,
              m: 0,
              borderRadius: 1,
              cursor: 'pointer',
              bgcolor: 'transparent',
              color: 'text.secondary',
              transition: 'color 0.15s',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <HomeRoundedIcon sx={{ fontSize: 17 }} />
          </Box>
          {items.length > 0 && Separator}
        </>
      )}

      {items.map((c, i) => {
        const isLast = i === items.length - 1;
        const isLink = !isLast && (!!c.to || !!c.onClick);
        const handleClick = () => {
          if (c.onClick) c.onClick();
          else if (c.to) navigate(c.to);
        };

        const node = isLink ? (
          <Box
            component="button"
            onClick={handleClick}
            sx={{
              border: 0,
              p: 0,
              m: 0,
              bgcolor: 'transparent',
              cursor: 'pointer',
              maxWidth: 220,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.8rem',
              fontWeight: 600,
              fontFamily: 'inherit',
              // 中間リンクは主テキスト色で明確に見えるように（背景との被り防止）
              color: 'text.primary',
              transition: 'color 0.15s',
              '&:hover': { color: 'primary.main', textDecoration: 'underline' },
            }}
          >
            {c.label}
          </Box>
        ) : (
          <Typography
            variant="caption"
            color={isLast ? 'text.primary' : 'text.secondary'}
            fontWeight={isLast ? 700 : 600}
            noWrap
            sx={{ maxWidth: 280, fontSize: '0.8rem' }}
          >
            {c.label}
          </Typography>
        );

        return (
          <React.Fragment key={i}>
            {i > 0 && Separator}
            {node}
          </React.Fragment>
        );
      })}
    </Stack>
  );
};

export default DomainBreadcrumbs;
